import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Plus, ChevronDown, DollarSign, Upload, FileDown, Printer, SlidersHorizontal,
  Tags, FileSpreadsheet, MoreHorizontal,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface MenuItemProps {
  icon: ReactNode;
  iconBg: string;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}

function MenuItem({ icon, iconBg, title, description, onClick, disabled }: MenuItemProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="text-xs text-gray-500 leading-snug mt-0.5">{description}</p>
      </div>
    </button>
  );
}

interface DropdownProps {
  label: string;
  icon: ReactNode;
  accent: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
  menuWidth?: string;
}

function ToolbarDropdown({ label, icon, accent, open, onToggle, children, menuWidth = 'w-72' }: DropdownProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onToggle();
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, onToggle]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={onToggle}
        className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold border transition-colors ${accent} ${open ? 'ring-2 ring-primary-500/20' : ''}`}
      >
        {icon}
        <span className="hidden sm:inline">{label}</span>
        <ChevronDown className={`w-4 h-4 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`absolute left-0 mt-2 ${menuWidth} bg-white border border-gray-100 rounded-xl shadow-xl z-40 p-1.5`}>
          {children}
        </div>
      )}
    </div>
  );
}

export interface ItemsPageToolbarProps {
  canManagePrices: boolean;
  isAdmin: boolean;
  brandModelVisible: boolean;
  canAddProducts: boolean;
  onAddItem: () => void;
  onAddCategory: () => void;
  onAddBrand: () => void;
  onAddModel: () => void;
  onUpdatePrices: () => void;
  onBulkUpdateExcel: () => void;
  onExportCatalog: () => void;
  onImport: () => void;
  onBulkPrint: () => void;
  onCustomFields: () => void;
  onExportData: () => void;
}

export default function ItemsPageToolbar({
  canManagePrices,
  isAdmin,
  brandModelVisible,
  canAddProducts,
  onAddItem,
  onAddCategory,
  onAddBrand,
  onAddModel,
  onUpdatePrices,
  onBulkUpdateExcel,
  onExportCatalog,
  onImport,
  onBulkPrint,
  onCustomFields,
  onExportData,
}: ItemsPageToolbarProps) {
  const { t } = useTranslation();
  const [openMenu, setOpenMenu] = useState<'add' | 'pricing' | 'print' | 'more' | null>(null);
  const addRef = useRef<HTMLDivElement>(null);

  const close = () => setOpenMenu(null);
  const toggle = (key: typeof openMenu) => setOpenMenu((m) => (m === key ? null : key));
  const run = (fn: () => void) => { close(); fn(); };

  useEffect(() => {
    if (openMenu !== 'add') return;
    const handler = (e: MouseEvent) => {
      if (addRef.current && !addRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenu]);

  return (
    <div className="flex flex-wrap items-center gap-2 p-2 bg-white border border-gray-200 rounded-xl shadow-sm">
      {/* Add — primary retail action */}
      <div className="relative" ref={addRef}>
        <button
          type="button"
          onClick={() => toggle('add')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          {t('common.add')}
          <ChevronDown className={`w-4 h-4 transition-transform ${openMenu === 'add' ? 'rotate-180' : ''}`} />
        </button>
        {openMenu === 'add' && (
          <div className="absolute left-0 mt-2 w-52 bg-white border border-gray-100 rounded-xl shadow-xl z-40 overflow-hidden py-1">
            <button
              type="button"
              disabled={!canAddProducts}
              onClick={() => run(onAddItem)}
              className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-primary-50 hover:text-primary-700 disabled:opacity-50"
            >
              {t('items.addItem')}
            </button>
            <button type="button" onClick={() => run(onAddCategory)} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700">
              {t('items.addCategory')}
            </button>
            {brandModelVisible && (
              <>
                <button type="button" onClick={() => run(onAddBrand)} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700">
                  {t('items.addBrand')}
                </button>
                <button type="button" onClick={() => run(onAddModel)} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700">
                  {t('items.addModel')}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="hidden sm:block w-px h-8 bg-gray-200 mx-0.5" />

      {canManagePrices && (
        <ToolbarDropdown
          label={t('items.toolbar.pricing')}
          icon={<DollarSign className="w-4 h-4" />}
          accent="bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100"
          open={openMenu === 'pricing'}
          onToggle={() => toggle('pricing')}
        >
          <MenuItem
            icon={<DollarSign className="w-4 h-4 text-emerald-700" />}
            iconBg="bg-emerald-100"
            title={t('items.updatePrices')}
            description={t('items.toolbar.updatePricesDesc')}
            onClick={() => run(onUpdatePrices)}
          />
          <MenuItem
            icon={<FileSpreadsheet className="w-4 h-4 text-emerald-700" />}
            iconBg="bg-emerald-100"
            title={t('items.bulkUpdate')}
            description={t('items.toolbar.bulkUpdateDesc')}
            onClick={() => run(onBulkUpdateExcel)}
          />
        </ToolbarDropdown>
      )}

      <ToolbarDropdown
        label={t('items.toolbar.printExport')}
        icon={<Printer className="w-4 h-4" />}
        accent="bg-sky-50 text-sky-800 border-sky-200 hover:bg-sky-100"
        open={openMenu === 'print'}
        onToggle={() => toggle('print')}
      >
        <MenuItem
          icon={<Printer className="w-4 h-4 text-sky-700" />}
          iconBg="bg-sky-100"
          title={t('items.bulkPrint')}
          description={t('items.toolbar.bulkPrintDesc')}
          onClick={() => run(onBulkPrint)}
        />
        {canManagePrices && (
          <MenuItem
            icon={<FileDown className="w-4 h-4 text-sky-700" />}
            iconBg="bg-sky-100"
            title={t('items.exportCatalog')}
            description={t('items.toolbar.exportCatalogDesc')}
            onClick={() => run(onExportCatalog)}
          />
        )}
      </ToolbarDropdown>

      <button
        type="button"
        onClick={() => run(onImport)}
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold bg-violet-50 text-violet-800 border border-violet-200 hover:bg-violet-100 transition-colors"
      >
        <Upload className="w-4 h-4" />
        <span className="hidden sm:inline">{t('items.toolbar.importProducts')}</span>
        <span className="sm:hidden">{t('common.import')}</span>
      </button>

      {isAdmin && (
        <button
          type="button"
          onClick={() => run(onExportData)}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-100 transition-colors"
        >
          <FileDown className="w-4 h-4" />
          <span className="hidden sm:inline">Backup Data</span>
          <span className="sm:hidden">Backup</span>
        </button>
      )}

      {isAdmin && (
        <ToolbarDropdown
          label={t('items.toolbar.more')}
          icon={<MoreHorizontal className="w-4 h-4" />}
          accent="bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
          open={openMenu === 'more'}
          onToggle={() => toggle('more')}
          menuWidth="w-64"
        >
          <MenuItem
            icon={<SlidersHorizontal className="w-4 h-4 text-gray-600" />}
            iconBg="bg-gray-100"
            title={t('items.customFields')}
            description={t('items.toolbar.customFieldsDesc')}
            onClick={() => run(onCustomFields)}
          />
        </ToolbarDropdown>
      )}

      {/* Quick hint for managers — desktop only */}
      {canManagePrices && (
        <p className="hidden lg:block ml-auto text-xs text-gray-400 pr-1">
          <Tags className="w-3.5 h-3.5 inline -mt-0.5 mr-1 opacity-60" />
          {t('items.toolbar.hint')}
        </p>
      )}
    </div>
  );
}
